/**
 * Who is signed in, asked once and shared by the whole shell.
 *
 * A context rather than a `useApi` call in each page, for one reason: the answer decides
 * whether the app renders AT ALL, and every page below it can then assume there is a
 * user. Threading a possibly-null user through seven screens is how a dashboard ends up
 * with a `user?.role === "internal"` check in twenty places and one of them wrong.
 *
 * WHAT IT DOES NOT DO IS ENFORCE ANYTHING. Drawing a fixed agent name instead of a
 * switcher is not what keeps a member inside their own organization - the Worker's access
 * clauses are, and they bind a member's session to its own `organization_id` in the statement
 * itself whatever this file believes. What this is for is not offering somebody a control
 * that would 404 if they used it.
 */

import { createContext, useCallback, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { ApiError, api, post, type Me } from "./api";

interface SessionState {
  me: Me | null;
  loading: boolean;
  /** Anything other than "not signed in" - a Worker that is down, most likely. */
  error: ApiError | null;
  reload: () => void;
  signOut: () => Promise<void>;
}

const Session = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let live = true;
    setLoading(true);
    api<Me>("/v1/auth/me")
      .then((result) => {
        if (!live) return;
        setMe(result);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!live) return;
        setMe(null);
        // A 401 is not an error to show anybody. It is the ordinary state of a browser
        // that has not signed in yet, and the answer to it is the sign-in form.
        setError(err instanceof ApiError && err.status === 401 ? null : (err as ApiError));
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  const signOut = useCallback(async () => {
    // The server revokes the session row; clearing the cookie alone would be theatre,
    // since the token stays valid for thirty days and anything that captured it works.
    await post("/v1/auth/signout", {}).catch(() => {});
    setMe(null);
    setNonce((n) => n + 1);
  }, []);

  return (
    <Session.Provider value={{ me, loading, error, reload, signOut }}>{children}</Session.Provider>
  );
}

export function useSession(): SessionState {
  const value = useContext(Session);
  if (!value) throw new Error("useSession outside a SessionProvider");
  return value;
}
